<template>
  <div v-if="userParameters !== null && transitUrlWithManifest !== null">
    <Dialog
      :key="dialogKey"
      :visible="modelValue"
      modal
      closable
      @update:visible="($emit('update:modelValue', $event), (transitFieldsLoaded = false))"
      :header="$t('c.fundingForms.transitDialog.paymentProfile')"
      class="pl-2 pr-2"
      :style="{ width: '95vw', maxWidth: '24rem' }"
      :breakpoints="{ '640px': '95vw' }"
    >
      <div v-if="!transitFieldsLoaded && !loadFailed" class="flex justify-content-center col-12">
        <ProgressSpinner class="w-4rem" stroke-width="5" />
      </div>
      <!-- DTA-1029: the card form never arrived (the gateway refused or did not answer).
           Say so instead of spinning for ever. -->
      <div
        v-if="loadFailed"
        class="col-12 text-center"
        role="alert"
        data-testid="transit-load-failed"
      >
        <p class="mb-3">{{ $t('c.fundingForms.transitDialog.cardFormUnavailable') }}</p>
        <Button :label="$t('c.fundingForms.transitDialog.tryAgainLabel')" @click="reset()" />
      </div>
      <div v-show="transitFieldsLoaded">
        <div>
          <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.firstNameLabel') }}:</div>
          <InputText
            class="mb-2 w-full md:w-16rem"
            v-model="userParameters.firstName"
            inputId="first-name"
            type="text"
            maxlength="25"
          />
          <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.lastNameLabel') }}:</div>
          <InputText
            class="mb-2 w-full md:w-16rem"
            v-model="userParameters.lastName"
            inputId="last-name"
            type="text"
            maxlength="25"
          />
          <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.addressLabel') }}:</div>
          <InputText
            class="mb-2 w-full md:w-16rem"
            v-model="userParameters.address1"
            inputId="address"
            type="text"
          />
          <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.cityLabel') }}:</div>
          <InputText
            class="mb-2 w-full md:w-16rem"
            v-model="userParameters.city"
            inputId="city"
            type="text"
          />
          <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.stateLabel') }}:</div>
          <Select
            v-model="userParameters.state"
            :options="states"
            optionLabel="label"
            class="mb-2 w-full md:w-16rem"
            optionValue="value"
            :placeholder="$t('c.fundingForms.transitDialog.selectAState')"
            filter
          />
          <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.zipCodeLabel') }}:</div>
          <InputText
            class="mb-2 w-full md:w-16rem"
            v-model="userParameters.zipCode"
            inputId="zip-code"
            :useGrouping="false"
            maxlength="5"
            inputmode="numeric"
          />
          <div class="mb-1 font-bold">
            {{ $t('c.fundingForms.transitDialog.cardNumberLabel') }}:
          </div>
          <div id="tsep-cardNumDiv" class="tsep-field-container" ref="tsep-cardNum"></div>

          <div class="mb-1 font-bold">
            {{ $t('c.fundingForms.transitDialog.expirationDateLabel') }}:
          </div>
          <div id="tsep-datepickerDiv" class="tsep-field-container"></div>

          <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.cvvLabel') }}:</div>
          <div id="tsep-cvv2Div" class="tsep-field-container"></div>
        </div>
        <div class="flex justify-between mt-2 mb-2 gap-2">
          <Button
            :label="$t('c.fundingForms.transitDialog.saveLabel')"
            @click="save()"
            class="save-button w-8rem"
            raised
          />
          <Button
            :label="$t('c.fundingForms.transitDialog.cancelLabel')"
            @click="($emit('update:modelValue', false), (transitFieldsLoaded = false))"
            text
            raised
            class="w-8rem"
          />
          <Button
            :label="$t('c.fundingForms.transitDialog.resetLabel')"
            @click="reset()"
            raised
            text
            class="w-8rem"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { API } from '@/modules/api'
import type { UserData } from '@/modules/api/get/userData'
import type { TransitCardRateLimitError } from '@/modules/api/post/transitCardData'
import { useI18n } from 'vue-i18n'

// DTA-1029: how long the card fields may take to appear before the window says the
// card form could not be loaded.
const TRANSIT_FIELDS_TIMEOUT_MS = 20_000

const isRateLimitError = (err: unknown): err is TransitCardRateLimitError => {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { isRateLimit?: boolean }).isRateLimit === true
  )
}

export default {
  name: 'TransitDialog',
  components: {},

  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    transitUrl: {
      type: [String, null],
      required: true
    },
    transitUrlWithManifest: {
      type: String,
      required: true
    },
    environmentId: {
      type: [Number, null],
      required: true
    },
    initialUserData: {
      type: [Object, null] as unknown as () => UserData | null,
      default: null
    },
    saveHandler: {
      type: [Function, null] as unknown as () => ((payload: object) => Promise<void>) | null,
      default: null
    }
  },

  computed: {},

  data() {
    return {
      userParameters: null as UserData | null,
      transitFieldsLoaded: false,
      // DTA-1029: the card fields did not appear in time, or their script failed.
      loadFailed: false,
      readyTimer: null as ReturnType<typeof setInterval> | null,
      dialogKey: 0,
      cardKeydownFired: false,
      states: [
        { label: 'Alabama', value: 'AL' },
        { label: 'Alaska', value: 'AK' },
        { label: 'Arizona', value: 'AZ' },
        { label: 'Arkansas', value: 'AR' },
        { label: 'California', value: 'CA' },
        { label: 'Colorado', value: 'CO' },
        { label: 'Connecticut', value: 'CT' },
        { label: 'Delaware', value: 'DE' },
        { label: 'District of Columbia', value: 'DC' },
        { label: 'Florida', value: 'FL' },
        { label: 'Georgia', value: 'GA' },
        { label: 'Hawaii', value: 'HI' },
        { label: 'Idaho', value: 'ID' },
        { label: 'Illinois', value: 'IL' },
        { label: 'Indiana', value: 'IN' },
        { label: 'Iowa', value: 'IA' },
        { label: 'Kansas', value: 'KS' },
        { label: 'Kentucky', value: 'KY' },
        { label: 'Louisiana', value: 'LA' },
        { label: 'Maine', value: 'ME' },
        { label: 'Maryland', value: 'MD' },
        { label: 'Massachusetts', value: 'MA' },
        { label: 'Michigan', value: 'MI' },
        { label: 'Minnesota', value: 'MN' },
        { label: 'Mississippi', value: 'MS' },
        { label: 'Missouri', value: 'MO' },
        { label: 'Montana', value: 'MT' },
        { label: 'Nebraska', value: 'NE' },
        { label: 'Nevada', value: 'NV' },
        { label: 'New Hampshire', value: 'NH' },
        { label: 'New Jersey', value: 'NJ' },
        { label: 'New Mexico', value: 'NM' },
        { label: 'New York', value: 'NY' },
        { label: 'North Carolina', value: 'NC' },
        { label: 'North Dakota', value: 'ND' },
        { label: 'Ohio', value: 'OH' },
        { label: 'Oklahoma', value: 'OK' },
        { label: 'Oregon', value: 'OR' },
        { label: 'Pennsylvania', value: 'PA' },
        { label: 'Rhode Island', value: 'RI' },
        { label: 'South Carolina', value: 'SC' },
        { label: 'South Dakota', value: 'SD' },
        { label: 'Tennessee', value: 'TN' },
        { label: 'Texas', value: 'TX' },
        { label: 'Utah', value: 'UT' },
        { label: 'Vermont', value: 'VT' },
        { label: 'Virginia', value: 'VA' },
        { label: 'Washington', value: 'WA' },
        { label: 'West Virginia', value: 'WV' },
        { label: 'Wisconsin', value: 'WI' },
        { label: 'Wyoming', value: 'WY' }
      ]
    }
  },

  methods: {
    async save() {
      if (this.hasErrors()) {
        return
      }
      this.$toast.add({
        severity: 'success',
        summary: this.$t('c.fundingForms.transitDialog.success'),
        detail: this.$t('c.fundingForms.transitDialog.savingData'),
        life: 3000
      })
      await this.sleep(3000)
      if (this.hasTransitErrors()) {
        return
      }
      const payload = {
        token: window.tsepToken,
        type: 'credit_card',
        environment_id: this.environmentId,
        last_4: window.last4,
        expiration_month: window.expDate,
        card_type: window.cardType,
        billing_address: {
          given_name: this.userParameters?.firstName,
          surname: this.userParameters?.lastName,
          address_1: this.userParameters?.address1,
          city: this.userParameters?.city,
          region: this.userParameters?.state,
          postal_code: String(this.userParameters?.zipCode),
          country: 'US'
        }
      }
      const savePromise = this.saveHandler
        ? this.saveHandler(payload)
        : API.Post.transitCardData(payload)
      savePromise
        .then(() => {
          this.$toast.add({
            severity: 'success',
            summary: this.$t('c.fundingForms.transitDialog.success'),
            detail: this.$t('c.fundingForms.transitDialog.savedSuccessfully'),
            life: 6000
          })
          this.$emit('update:modelValue', false)
          this.$emit('funding-info-saved')
        })
        .catch(err => {
          console.error(err)
          if (isRateLimitError(err)) {
            this.$toast.add({
              severity: 'error',
              summary: this.$t('c.fundingForms.transitDialog.error'),
              detail: this.$t('c.fundingForms.transitDialog.rateLimitExceeded', {
                hours: this.formatHoursUntilReset(err.retryAfterSeconds)
              }),
              life: 10000,
              styleClass: 'transit-rate-limit-toast'
            })
            return
          }
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: this.$t('c.fundingForms.transitDialog.failedSaveCreditCard'),
            life: 6000
          })
        })
    },
    sleep(ms: number) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    formatHoursUntilReset(retryAfterSeconds: number): string {
      const safeSeconds = Math.max(0, Math.ceil(retryAfterSeconds))
      if (safeSeconds < 60) {
        return this.$t('c.fundingForms.transitDialog.lessThanAMinute')
      }
      const minutes = Math.ceil(safeSeconds / 60)
      if (minutes < 60) {
        return this.$t('c.fundingForms.transitDialog.durationMinutes', { count: minutes })
      }
      const hours = Math.ceil(minutes / 60)
      return this.$t('c.fundingForms.transitDialog.durationHours', { count: hours })
    },
    getUserData() {
      if (this.initialUserData) {
        this.userParameters = { ...this.initialUserData }
        return
      }
      API.Get.userData()
        .then(results => {
          this.userParameters = results
        })
        .catch(err => {
          console.error(err)
        })
    },
    reset() {
      this.cardKeydownFired = false
      this.loadFailed = false
      this.loadScript()
      this.dialogKey++
      this.transitFieldsLoaded = false
    },
    loadScript() {
      new Promise<void>(resolve => setTimeout(resolve, 500)).finally(() => {
        if (!this.transitUrlWithManifest) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('c.fundingForms.transitDialog.error'),
            detail: this.$t('c.fundingForms.transitDialog.cannotLoadCreditCardFields'),
            life: 6000
          })
          this.loadFailed = true
          throw new Error('Cannot load script, transitUrlWithManifest is undefined')
        }

        const script = document.createElement('script')
        script.src = this.transitUrlWithManifest
        script.id = 'transit-url-script'
        script.async = true
        script.onload = () => {
          this.checkTransitFieldsReady()
        }
        script.onerror = () => {
          this.markLoadFailed()
        }
        document.body.appendChild(script)

        const script1 = document.createElement('script')
        script.src = this.transitUrl + '/transit-tsep-web/resources/jsencrypt.js'
        script.id = 'transit-url-script2'
        script.async = true
        document.body.appendChild(script1)

        const script2 = document.createElement('script')
        script.src = this.transitUrl + '/transit-tsep-web/resources/tsep-new-min.js?version=3'
        script.id = 'transit-url-script3'
        script.async = true
        document.body.appendChild(script2)
      })
    },
    hasTransitErrors() {
      if (window.errorMsg != '' && window.errorMsg) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: window.errorMsg,
          life: 6000
        })
        if (window.responseCode == 'TSEP001') {
          this.loadScript()
          this.dialogKey++
        }
        return true
      }
      if (window.tsepToken === '') {
        const cardInput = document.querySelector('#tsep-cardNumDiv input') as HTMLInputElement
        const likelyAutofill = !this.cardKeydownFired && (cardInput?.value?.length ?? 0) > 0
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.transitDialog.error'),
          detail: likelyAutofill
            ? this.$t('c.fundingForms.transitDialog.creditCardAutoFillNotSupported')
            : this.$t('c.fundingForms.transitDialog.creditCardNotCapturedOrInvalid'),
          life: 6000
        })
        return true
      }
      return false
    },
    hasErrors() {
      let hasError = false
      if (!this.userParameters?.zipCode) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.transitDialog.error'),
          detail: this.$t('c.fundingForms.transitDialog.zipCodeRequired'),
          life: 6000
        })
        hasError = true
      }
      if (!this.userParameters?.zipCode?.toString().match(/^\d{5}$/)) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.transitDialog.error'),
          detail: this.$t('c.fundingForms.transitDialog.zipCodeInvalid'),
          life: 6000
        })
        hasError = true
      }
      if (
        this.userParameters?.zipCode &&
        (this.userParameters.zipCode.toString().length < 5 ||
          this.userParameters.zipCode.toString().length > 5)
      ) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.transitDialog.error'),
          detail: this.$t('c.fundingForms.transitDialog.zipCodeFiveDigits'),
          life: 6000
        })
        hasError = true
      }
      const cardNumElement = document.getElementById('tsep-cardNum') as HTMLInputElement
      if (cardNumElement?.value == '') {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.transitDialog.error'),
          detail: this.$t('c.fundingForms.transitDialog.creditCardNumberRequired'),
          life: 6000
        })
        hasError = true
      }
      const datepickerElement = document.getElementById('tsep-datepicker') as HTMLInputElement
      if (datepickerElement?.value == '') {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.transitDialog.error'),
          detail: this.$t('c.fundingForms.transitDialog.expirationDateRequired'),
          life: 6000
        })
        hasError = true
      }
      const cvv2Element = document.getElementById('tsep-cvv2') as HTMLInputElement
      if (cvv2Element?.value == '') {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.transitDialog.error'),
          detail: this.$t('c.fundingForms.transitDialog.cvvRequired'),
          life: 6000
        })
        hasError = true
      }

      return hasError
    },
    checkTransitFieldsReady() {
      this.stopReadyCheck()
      // DTA-1029: a gateway that refuses the form (wrong credentials, outage) never
      // draws the fields, and this used to poll for ever behind a spinner.
      const deadline = Date.now() + TRANSIT_FIELDS_TIMEOUT_MS
      this.readyTimer = setInterval(() => {
        const card = document.getElementById('tsep-cardNumDiv')?.children.length
        const date = document.getElementById('tsep-datepickerDiv')?.children.length
        const cvv = document.getElementById('tsep-cvv2Div')?.children.length

        if (card && date && cvv) {
          this.transitFieldsLoaded = true
          this.stopReadyCheck()
          this.setupAutofillDetection()
        } else if (Date.now() > deadline) {
          this.markLoadFailed()
        }
      }, 100)
    },
    stopReadyCheck() {
      if (this.readyTimer !== null) {
        clearInterval(this.readyTimer)
        this.readyTimer = null
      }
    },
    markLoadFailed() {
      this.stopReadyCheck()
      if (!this.transitFieldsLoaded) {
        this.loadFailed = true
      }
    },
    setupAutofillDetection() {
      this.cardKeydownFired = false
      const cardInput = document.querySelector('#tsep-cardNumDiv input') as HTMLInputElement
      if (!cardInput) return
      cardInput.addEventListener('keydown', (e: KeyboardEvent) => {
        // e.key.length === 1 means a printable character (digit, letter, symbol).
        // Autofill-triggered keydown events have e.key = "Unidentified", "Process", etc.
        // which are all longer than 1 character, so they won't set this flag.
        if (e.key && e.key.length === 1) {
          this.cardKeydownFired = true
        }
      })
    }
  },

  mounted() {
    this.transitFieldsLoaded = false
  },
  beforeUnmount() {
    this.stopReadyCheck()
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.getUserData()
        this.loadScript()
      } else {
        this.cardKeydownFired = false
        this.loadFailed = false
        this.stopReadyCheck()
      }
    }
  }
}
</script>

<style>
.dialog-container {
  padding-left: 1.25rem;
}

.tsep-field-container input {
  font-size: 1rem;
  width: 100%;
  max-width: 16rem;
  box-sizing: border-box;
  color: var(--p-inputtext-color);
  border-radius: var(--p-inputtext-border-radius);
  padding-block: var(--p-inputtext-padding-y);
  padding-inline: var(--p-inputtext-padding-x);
  border: 1px solid var(--p-inputtext-border-color);
  box-shadow: var(--p-inputtext-shadow);
  transition:
    background var(--p-inputtext-transition-duration),
    color var(--p-inputtext-transition-duration),
    border-color var(--p-inputtext-transition-duration),
    outline-color var(--p-inputtext-transition-duration),
    box-shadow var(--p-inputtext-transition-duration);
  margin-bottom: 0.5rem;
}

/* When the rate-limit toast is shown, lift the toast container above
   the parent AddFundingSourceDialog's modal mask (:baseZIndex="99999"). */
.p-toast:has(.transit-rate-limit-toast) {
  z-index: 1000000 !important;
}
</style>
